import { ErrorHandler, Injectable } from '@angular/core'

import { DataSource } from '@angular/cdk/table'
import { PageEvent } from '@angular/material/paginator'

import { BehaviorSubject, combineLatest, Observable, of as observableOf } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { NumberMonth } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { LaskuTransformingWrappingDataSource } from '../../_jaettu-angular/laskut/transforming-lasku.datasource'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

import { Lasku, LaskunTila } from '@lemon/core-firebase/model/lasku'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { DocumentSnapshot } from 'firebase/firestore'
import { annaKuukaudenPaivarajat, annaRajattavatKuukaudet } from './laskut-selaa.logiikka'

export interface LaskunLokalisoituTila {
  nimi: string
  tunnus: string
}

export interface LaskunLokalisoituKuukausi {
  nimi: string
  tunnus: NumberMonth | null
}

export interface Hakukriteerit {
  tila: LaskunTila
  kuukausi: NumberMonth | null
}

interface Hakutiedot {
  hakukriteerit: Hakukriteerit
  sivutustiedot: PageEvent
}

@Injectable()
export class LaskutSelaaDataSourceService extends DataSource<Lasku> {
  private rivienMaaraSubject = new BehaviorSubject(Number.MAX_SAFE_INTEGER)
  public rivienMaaraObservable: Observable<number> = this.rivienMaaraSubject.asObservable()

  public laskunTilatObservable: Observable<LaskunLokalisoituTila[]>
  public kuukausivaihtoehdotObservable: Observable<LaskunLokalisoituKuukausi[]>

  private internalLaskutObservable: Observable<Lasku[]>
  public laskutObservable: Observable<Lasku[]>

  dataSourceTransformed: LaskuTransformingWrappingDataSource = null

  private lataaSubject = new BehaviorSubject<boolean>(true)
  public lataaObservable: Observable<boolean> = this.lataaSubject.asObservable()

  private oletuskriteerit: Hakukriteerit = {
    tila: LaskunTila.kaikki,
    kuukausi: null,
  }

  private _kaikkiTilat = Object.values(LaskunTila).filter(tila => tila !== LaskunTila.kaikki)
  private _avoimetTilat = [LaskunTila.avoin, LaskunTila.eraantynyt, LaskunTila.luonnos]

  private oletussivutustiedot: PageEvent = {
    length: Number.MAX_SAFE_INTEGER,
    pageIndex: 0,
    pageSize: 20,
  }

  private oletusHakutiedot: Hakutiedot = {
    hakukriteerit: this.kopioiHakukriteerit(this.oletuskriteerit),
    sivutustiedot: this.kopioiSivutustiedot(this.oletussivutustiedot),
  }

  private hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this.oletusHakutiedot)
  public hakutiedotObservable = this.hakutiedotSubject.asObservable()

  private lastVisible: DocumentSnapshot<Lasku>[] = []

  constructor(
    private _errorHandler: ErrorHandler,
    private _laskuSharedService: LaskuSharedService,
    private _asiakasService: AsiakasService,
    private lemonTranslationService: LemonTranslationService,
    private _currencyService: CurrencyService,
    private dateService: DateService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
  ) {
    super()

    this.dataSourceTransformed = new LaskuTransformingWrappingDataSource(this, this._currencyService, this.dateService, this._laskuSharedService, this.lemonTranslationService)

    this.laskunTilatObservable = this.lemonTranslationService.currentLanguageObservable.pipe(
      map(kieli => {
        const tilat: LaskunLokalisoituTila[] = []
        tilat.push({ tunnus: LaskunTila.kaikki, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.kaikki, kieli) })
        tilat.push({ tunnus: LaskunTila.avoin, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.avoin, kieli) })
        tilat.push({ tunnus: LaskunTila.luottotappio, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.luottotappio, kieli) })
        tilat.push({ tunnus: LaskunTila.hyvitetty, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.hyvitetty, kieli) })
        tilat.push({ tunnus: LaskunTila.maksettu, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.maksettu, kieli) })
        tilat.push({ tunnus: LaskunTila.eraantynyt, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.eraantynyt, kieli) })
        tilat.push({ tunnus: LaskunTila.maksettuLiikaa, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.maksettuLiikaa, kieli) })
        tilat.push({ tunnus: LaskunTila.luonnos, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.luonnos, kieli) })
        tilat.push({ tunnus: LaskunTila.poistettu, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.poistettu, kieli) })
        tilat.push({ tunnus: LaskunTila.mitatoity, nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.mitatoity, kieli) })
        // tilat.push({ tunnus: LaskunTila.perinnassa,     nimi: this.lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + LaskunTila.perinnassa,     kieli) })

        return tilat.sort((a, b): number => {
          if (a.tunnus === LaskunTila.kaikki) {
            return -1
          } else if (b.tunnus === LaskunTila.kaikki) {
            return 1
          }
          return a.nimi.localeCompare(b.nimi)
        })
      }),
    )

    this.kuukausivaihtoehdotObservable = this.lemonTranslationService.currentLanguageObservable.pipe(
      map(kieli => {
        const kuukausivaihtoehdot = annaRajattavatKuukaudet(this.dateService.currentLocalMonth(), this.dateService).map(kuukausi => ({
          tunnus: this.dateService.localMonthToNumber(kuukausi),
          nimi: `${this.dateService.annaKuukaudenNimiPaikallinen(kuukausi, kieli)} ${kuukausi.year}`,
        }))
        return [{ tunnus: null, nimi: this.lemonTranslationService.lokalisoiKielella('laskut.kaikki-kuukaudet', kieli) }, ...kuukausivaihtoehdot]
      }),
    )

    this.internalLaskutObservable = combineLatest([this.hakutiedotSubject, this._asiakasService.nykyinenAsiakasAvainObservable]).pipe(
      tap(() => {
        this.lataaSubject.next(true)
      }),
      switchMap(([tiedot, asiakas]) => {
        const hakukriteerit = tiedot.hakukriteerit
        const pageEvent = tiedot.sivutustiedot
        if (!asiakas) {
          this.resetSearchToDefaults()
          return observableOf<Lasku[]>([])
        }
        const tila = hakukriteerit.tila
        const limitSize = pageEvent.pageSize + 1

        const uri = this._laskuUriService.getCustomerLaskutCollectionUri(asiakas.avain)
        let q = this._firebaseLemonaid.firestoreCollection<Lasku>(uri).where('listauksessa', '==', true)

        const naytetaankoAvoimet = tila === LaskunTila.avoin
        const rajataanTilalla = tila && tila !== LaskunTila.kaikki
        if (naytetaankoAvoimet) {
          q = q.whereFree('tila', 'in', this._avoimetTilat)
        } else if (rajataanTilalla) {
          q = q.whereFree('tila', '==', tila)
        } else {
          q = q.whereFree('tila', 'in', this._kaikkiTilat)
        }
        const kuukausi = hakukriteerit.kuukausi
        if (kuukausi) {
          const rajat = annaKuukaudenPaivarajat(kuukausi, this.dateService)
          q = q.whereFree('p', '>=', rajat.alkaen).whereFree('p', '<', rajat.ennen)
        }
        q = q.orderByFree('p', 'desc')

        if (pageEvent.pageIndex > 0 && pageEvent.pageIndex - 1 in this.lastVisible) {
          q = q.startAfter(this.lastVisible[pageEvent.pageIndex - 1]).limit(limitSize)
        } else {
          q = q.limit(limitSize)
        }

        return q.listenSnapshots().pipe(
          map(snapshots => {
            if (snapshots.length === limitSize) {
              this.rivienMaaraSubject.next(Number.MAX_SAFE_INTEGER)
              snapshots.pop()
            } else {
              this.rivienMaaraSubject.next(pageEvent.pageSize * pageEvent.pageIndex + snapshots.length)
            }
            if (snapshots.length > 0) {
              const snap = snapshots[snapshots.length - 1]
              this.lastVisible[pageEvent.pageIndex] = snap
            }
            return snapshots.map(changeAction => changeAction.data())
          }),
          map(laskut => {
            for (const lasku of laskut) {
              this._laskuSharedService.poistaKaikkiLaskunHakuKentatMuistista(lasku)
            }
            return laskut
          }),
          tap(() => {
            setTimeout(() => {
              this.lataaSubject.next(false)
            }, 0)
          }),
        )
      }),
      lemonShare(),
    )

    this.laskutObservable = combineLatest([this.internalLaskutObservable, this.lataaObservable]).pipe(
      map(([laskut, lataa]) => {
        if (lataa) {
          return []
        }
        return laskut
      }),
    )
  }

  private kopioiHakukriteerit(lahde: Hakukriteerit): Hakukriteerit {
    return {
      tila: lahde.tila,
      kuukausi: lahde.kuukausi,
    }
  }

  private kopioiSivutustiedot(lahde: PageEvent): PageEvent {
    return {
      length: lahde.length,
      pageIndex: lahde.pageIndex,
      pageSize: lahde.pageSize,
      previousPageIndex: lahde.previousPageIndex,
    }
  }

  set page(pageEvent: PageEvent) {
    const hakutiedot = this.hakutiedotSubject.value
    hakutiedot.sivutustiedot = pageEvent
    this.hakutiedotSubject.next(hakutiedot)
  }

  set tila(tila: LaskunTila) {
    const hakutiedot = this.hakutiedotSubject.value
    if (hakutiedot.hakukriteerit.tila !== tila) {
      this.valmistauduHakuehdonMuutokseen(hakutiedot)
      hakutiedot.hakukriteerit.tila = tila
      this.hakutiedotSubject.next(hakutiedot)
    }
  }

  get tila(): LaskunTila {
    return this.hakutiedotSubject.value.hakukriteerit.tila
  }

  set kuukausi(kuukausi: NumberMonth | null) {
    const hakutiedot = this.hakutiedotSubject.value
    if (hakutiedot.hakukriteerit.kuukausi !== kuukausi) {
      this.valmistauduHakuehdonMuutokseen(hakutiedot)
      hakutiedot.hakukriteerit.kuukausi = kuukausi
      this.hakutiedotSubject.next(hakutiedot)
    }
  }

  get kuukausi(): NumberMonth | null {
    return this.hakutiedotSubject.value.hakukriteerit.kuukausi
  }

  get page(): PageEvent {
    return this.hakutiedotSubject.value.sivutustiedot
  }

  resetSearchToDefaults() {
    const hakutiedot = this.hakutiedotSubject.value
    const hakukriteerit = this.kopioiHakukriteerit(this.oletuskriteerit)
    const sivutustiedot = this.kopioiSivutustiedot(this.oletussivutustiedot)

    // console.log('RESET')
    // If this is not here, a forever loop is reached when user logs out.
    if (JSON.stringify(hakutiedot.hakukriteerit) !== JSON.stringify(hakukriteerit) || JSON.stringify(hakutiedot.sivutustiedot) !== JSON.stringify(sivutustiedot)) {
      // console.log('RESET CHANGED')
      hakutiedot.hakukriteerit = hakukriteerit
      hakutiedot.sivutustiedot = sivutustiedot
      this.lastVisible = []
      this.rivienMaaraSubject.next(Number.MAX_SAFE_INTEGER)
      this.hakutiedotSubject.next(hakutiedot)
    }
  }

  private valmistauduHakuehdonMuutokseen(hakutiedot: Hakutiedot) {
    this.lastVisible = []
    hakutiedot.sivutustiedot.pageIndex = 0
    delete hakutiedot.sivutustiedot.previousPageIndex
    hakutiedot.sivutustiedot.length = Number.MAX_SAFE_INTEGER
    this.rivienMaaraSubject.next(Number.MAX_SAFE_INTEGER)
  }

  connect(): Observable<Lasku[]> {
    this.lataaSubject.next(true)
    return this.laskutObservable
  }

  disconnect() {}
}
